import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import AuthModal from '../auth/AuthModal';
import MegaMenu from './MegaMenu';
import MobileNav from './MobileNav';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMegaOpen, setIsMegaOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('token');
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'glass-heavy' : 'bg-transparent'
        }`}
      >
        <nav className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-6">
          <div className="flex items-center justify-between gap-6">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo-link">
              <div className="text-2xl font-medium leading-none" style={{ fontFamily: 'Playfair Display, serif' }}>
                <span className="text-gold">Southeast Asia</span>
                <br />
                <span className="text-white text-[10px] md:text-xs font-light tracking-[0.3em]">LUXURY YACHTS</span>
              </div>
            </Link>

            {/* Desktop Mega Menu */}
            <div className="hidden xl:flex flex-1 justify-center">
              <MegaMenu onOpenChange={setIsMegaOpen} />
            </div>

            {/* Right actions */}
            <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
              <Link to="/list/new" data-testid="nav-list-with-us">
                <Button
                  variant="ghost"
                  className="text-white/70 hover:text-gold text-xs uppercase tracking-[0.2em] border border-white/15 hover:border-gold/50 px-4"
                >
                  <Plus size={14} className="mr-1.5" /> List
                </Button>
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" data-testid="nav-dashboard">
                    <Button variant="ghost" className="text-gold hover:text-gold/80 text-xs uppercase tracking-[0.2em]">
                      Dashboard
                    </Button>
                  </Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" data-testid="nav-admin">
                      <Button variant="ghost" className="text-white/70 hover:text-gold text-xs uppercase tracking-[0.2em]">
                        Admin
                      </Button>
                    </Link>
                  )}
                  <Button onClick={handleLogout} variant="ghost" className="text-white/60 text-xs uppercase tracking-[0.2em]" data-testid="logout-btn">
                    Logout
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setShowAuthModal(true)}
                  className="bg-gold text-[#0A192F] hover:bg-[#E5C55D] text-xs uppercase tracking-[0.2em] px-5"
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden text-white"
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div
              className="xl:hidden mt-6 p-6 max-h-[75vh] overflow-y-auto border border-white/10 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)]"
              style={{ backgroundColor: 'rgba(5, 12, 22, 0.98)', backdropFilter: 'blur(24px)' }}
              data-testid="mobile-menu"
            >
              <MobileNav onNavigate={() => setIsMenuOpen(false)} />

              <div className="mt-5 pt-5 border-t border-white/10 flex flex-col gap-3">
                <Link to="/list/new" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-list">
                  <Button className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D] text-xs uppercase tracking-[0.2em]">
                    <Plus size={14} className="mr-1.5" /> List with us
                  </Button>
                </Link>
                <Link to="/yacht-finder" onClick={() => setIsMenuOpen(false)} className="text-white/80 hover:text-gold text-xs uppercase tracking-[0.2em]" data-testid="mobile-nav-yacht-finder">
                  Find Your Yacht
                </Link>
                <Link to="/calculator" onClick={() => setIsMenuOpen(false)} className="text-white/80 hover:text-gold text-xs uppercase tracking-[0.2em]" data-testid="mobile-nav-calculator">
                  Calculator
                </Link>
                <Link to="/about" onClick={() => setIsMenuOpen(false)} className="text-white/80 hover:text-gold text-xs uppercase tracking-[0.2em]" data-testid="mobile-nav-about">
                  About
                </Link>
                <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="text-white/80 hover:text-gold text-xs uppercase tracking-[0.2em]" data-testid="mobile-nav-contact">
                  Contact
                </Link>

                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setIsMenuOpen(false)} className="text-gold text-xs uppercase tracking-[0.2em]" data-testid="mobile-nav-dashboard">
                      Dashboard
                    </Link>
                    {user.role === 'admin' && (
                      <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="text-white/80 hover:text-gold text-xs uppercase tracking-[0.2em]" data-testid="mobile-nav-admin">
                        Admin
                      </Link>
                    )}
                    <button onClick={handleLogout} className="text-white/60 text-left text-xs uppercase tracking-[0.2em]" data-testid="mobile-logout-btn">
                      Logout
                    </button>
                  </>
                ) : (
                  <Button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setShowAuthModal(true);
                    }}
                    className="bg-white/5 hover:bg-white/10 text-white text-xs uppercase tracking-[0.2em] border border-white/10"
                    data-testid="mobile-login-btn"
                  >
                    Sign In
                  </Button>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={(userData) => {
            setUser(userData);
            setShowAuthModal(false);
          }}
        />
      )}
    </>
  );
}
